import { useMemo, useState } from "react";

import { appApi } from "../../core/api/app.js";
import { tagTone } from "../../core/format/tags.js";
import {
  addDays,
  dayLabelFull,
  money,
  monthLabel,
  monthOf,
  monthRange,
  shiftMonth,
  upTo,
  weekLabel,
  weekStart,
} from "../../core/format/periods.js";
import { useAppSession } from "../../state/AppSessionContext.jsx";
import { useEndpoint } from "../../state/useEndpoint.js";
import { Bars, Columns, Empty, Failed, Loading, Panel } from "../components/Panel.jsx";
import PeriodNav from "../components/PeriodNav.jsx";

const GROUPS = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

const SORTS = [
  { key: "date-desc", label: "Newest first" },
  { key: "date-asc", label: "Oldest first" },
  { key: "amount-desc", label: "Amount, high to low" },
  { key: "amount-asc", label: "Amount, low to high" },
  { key: "category-asc", label: "Category A–Z" },
];

function groupKeyOf(date, group) {
  if (group === "month") return monthOf(date);
  if (group === "week") return weekStart(date);
  return date;
}

function groupTitle(key, group, today) {
  if (group === "month") return monthLabel(key);
  if (group === "week") return weekLabel(key);
  return dayLabelFull(key, today);
}

function sortRows(rows, sort) {
  const [field, dir] = sort.split("-");
  const sign = dir === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    if (field === "amount") return (a.amount - b.amount) * sign;
    if (field === "category") return String(a.category ?? "").localeCompare(String(b.category ?? "")) * sign;
    return (a.date < b.date ? -1 : a.date > b.date ? 1 : 0) * sign;
  });
}

export default function Money() {
  const { token, me } = useAppSession();
  const today = me?.today ?? new Date().toISOString().slice(0, 10);
  const thisMonth = monthOf(today);

  const [month, setMonth] = useState(thisMonth);
  const [group, setGroup] = useState("day");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("date-desc");

  const { from, to } = monthRange(month);
  const { data, loading, error } = useEndpoint(() => appApi.expenses(token, from, to), [token, from, to]);

  const currency = data?.currency ?? me?.currency;
  const fmt = (v) => money(v, currency);

  const groups = useMemo(() => {
    if (!data) return [];

    const kept = category === "all" ? data.rows : data.rows.filter((r) => r.category === category);
    const buckets = new Map();

    for (const row of sortRows(kept, sort)) {
      const key = groupKeyOf(row.date, group);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(row);
    }

    const order = [...buckets.keys()].sort();
    if (sort !== "date-asc") order.reverse();

    return order.map((key) => ({
      key,
      rows: buckets.get(key),
      total: buckets.get(key).reduce((sum, r) => sum + (r.amount ?? 0), 0),
    }));
  }, [data, category, group, sort]);

  const spentOn = useMemo(
    () => new Map((data?.byDay ?? []).map((d) => [d.date, d.total])),
    [data],
  );

  const monthDays = useMemo(() => {
    const all = [];
    for (let date = from; date <= to; date = addDays(date, 1)) all.push(date);
    return upTo(all, today);
  }, [from, to, today]);

  const shownTotal = groups.reduce((sum, g) => sum + g.total, 0);
  const shownCount = groups.reduce((sum, g) => sum + g.rows.length, 0);

  return (
    <>
      <PeriodNav
        label={monthLabel(month)}
        onPrev={() => setMonth(shiftMonth(month, -1))}
        onNext={() => setMonth(shiftMonth(month, 1))}
        atLatest={month >= thisMonth}
        right={data ? `${fmt(data.total)} · ${data.count} entries` : null}
      />

      {loading && <Loading />}
      {error && <Failed error={error} />}

      {data && !loading && (
        <>
          <Panel title="Where it went">
            {data.byCategory.length === 0 ? (
              <Empty>Nothing spent this month.</Empty>
            ) : (
              <Bars rows={data.byCategory.map((c) => ({ label: c.category, value: c.total }))} format={fmt} />
            )}
          </Panel>

          <Panel title="Day by day">
            {data.byDay.length === 0 ? (
              <Empty>No days with spending.</Empty>
            ) : (
              <Columns
                rows={monthDays.map((date) => ({
                  label: date.slice(8).replace(/^0/, ""),
                  tooltip: dayLabelFull(date, today),
                  value: spentOn.get(date) ?? 0,
                }))}
                format={fmt}
              />
            )}
          </Panel>

          <Panel
            title="Every entry"
            meta={
              shownCount === data.count
                ? `${data.count} rows`
                : `${shownCount} of ${data.count} · ${fmt(shownTotal)}`
            }
          >
            <div className="controls">
              <label>
                Group by
                <select value={group} onChange={(e) => setGroup(e.target.value)}>
                  {GROUPS.map((g) => (
                    <option key={g.key} value={g.key}>
                      {g.label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Category
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="all">All</option>
                  {data.byCategory.map((c) => (
                    <option key={c.category} value={c.category}>
                      {c.category}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Sort
                <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  {SORTS.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {groups.length === 0 ? (
              <Empty>{data.count === 0 ? "Nothing logged." : "Nothing matches that filter."}</Empty>
            ) : (
              groups.map((bucket) => (
                <div className="group" key={bucket.key}>
                  <div className="group-head">
                    <span className="group-title">{groupTitle(bucket.key, group, today)}</span>
                    <span className="group-total">{fmt(bucket.total)}</span>
                  </div>
                  <table className="table is-entries">
                    <tbody>
                      {bucket.rows.map((row) => (
                        <tr key={row.id}>
                          <td className="mono soft cell-date">{row.date.slice(5)}</td>
                          <td className="cell-entry">
                            <span className="entry-name">{row.name}</span>
                            {row.category && (
                              <span className={`pill tone-${tagTone(row.category)}`}>{row.category}</span>
                            )}
                          </td>
                          <td className="soft cell-method">{row.paymentMethod ?? "—"}</td>
                          <td className="num mono cell-amount">{fmt(row.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))
            )}
          </Panel>
        </>
      )}
    </>
  );
}
